// src/hooks/useCustomerTree.ts

import { keepPreviousData, useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems } from '../support/queries';
import { CacheKey } from '../types/cache-keys';
import { CustomerTreeNode } from '../types/customer-tree';

export interface CustomerTreeApiEnvelope {
  items: CustomerTreeNode[];
  total?: number;
  page: number;
  per_page: number;
  from: number;
  to: number;
  metadata?: Record<string, any>;
}

interface TreeQueryParams {
  page: number;
  size: number;
  filters: any[];
  orderBy: { column: string; order: 'asc' | 'desc' } | null;
  columns: string[];
  search: string;
}

export const useCustomerTreePage = ({
                                      page,
                                      size,
                                      filters,
                                      orderBy,
                                      columns,
                                      search,
                                    }: TreeQueryParams) =>
  useQuery<CustomerTreeApiEnvelope>({
    queryKey: [CacheKey.AdminCustomerTree, page, size, filters, orderBy, columns, search],
    queryFn: async () => {
      const body = {
        filters,
        order_by: `${orderBy?.column ?? 'Name'} ${
          orderBy?.order?.toUpperCase() ?? 'ASC'
        }`,
        columns,
        search_string: search ?? '',
      };

      const rawEnvelope = await fetchItems(
        'sf/customer-tree',
        { page, size },
        { pagination: true, body },
      );

      const rawItems = (rawEnvelope.items ?? []) as any[];

      const mapNode = (n: any): CustomerTreeNode => ({
        id: n.Id,
        name: n.Name ?? null,
        parent_account_id: n.parent_account_id ?? null,
        parent_resolution_method: n.parent_resolution_method ?? null,
        parent_resolution_status: n.parent_resolution_status ?? null,
        parent_account_name: n.parent_account_name ?? null,
        children: (n.children ?? []).map(mapNode),
      });

      const mappedItems: CustomerTreeNode[] = rawItems.map(mapNode);

      return {
        items: mappedItems,
        // total should represent ROOTS count only
        total:
          rawEnvelope.total_roots ??
          rawEnvelope.total ??
          0,
        page: rawEnvelope.page ?? page,
        per_page: rawEnvelope.per_page ?? size,
        from: rawEnvelope.from ?? 0,
        to: rawEnvelope.to ?? 0,
        metadata: rawEnvelope.metadata,
      };
    },
    ...{
        placeholderData: keepPreviousData,
      },
  });